import type { GetWorklistViewsQuery } from 'generated/graphql';

import { SEARCH_DEBOUNCE_MS, SORTABLE_WORKLIST_COLUMN_MAP } from 'config/constants';
import { SortColumn, SortOrder, WorklistItemStatus } from 'generated/graphql';
import { useDebouncedState } from 'hooks/useDebouncedState';
import {
  DEFAULT_EMPTY_WORKLIST_SURFACE_FILTERS,
  DEFAULT_SORT_COLUMNS,
  DEFAULT_SORT_ORDERS,
  DEFAULT_WORKLIST_SURFACE_SEARCH_VALUES,
} from './constants';

import { assoc, equals } from 'ramda';
import {
  useContext,
  createContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  MutableRefObject,
} from 'react';
import { useWorklistURLParams } from '../Worklist/useWorklistURLParams';
import type {
  FilterDefinitionType,
  FilterStateBySurface,
  FilterStateType,
  MultiSelectFilterItemType,
  WorklistFiltersType,
  WorklistSurfaceSearchValues,
} from './types';
import { useGenerateFilterConfig } from './hooks/useGenerateFilterConfig';
import { FilterVariant } from './types';
import { logger } from 'modules/logger';
import { WorklistSurface, type WorklistSurfaceType } from '../Worklist/types';
import { useWorklistViews } from '../Worklist/WorklistViews/useWorklistViews';
import {
  SaveSortingPreferencesArgs,
  useSavedSortingPreferences,
} from '../Worklist/hooks/useSavedSortingPreferences';
import { useSavedWorklistViews } from '../Worklist/WorklistViews/useSavedWorklistViews';
import {
  getWorklistURLParams,
  parseFiltersFromURLParams,
  validateSortColumns,
  validateSortOrders,
} from './utils';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { useLazyQuery } from '@apollo/client';
import { GET_CUSTOM_WORKLIST } from 'modules/Apollo/queries';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { activeCustomWorklistFiltersState, activeCustomWorklistIDState } from '../Worklist/state';
import {
  formatCustomWorklistFilters,
  getCustomWorklistNames,
} from '../Worklist/WorklistPageV2/utils';
import { useSaveLastViewedCustomWorklists } from '../Worklist/hooks/useLastViewedCustomWorklists';
import { useMount } from 'react-use';
import { CUSTOM_WORKLIST_FILTERS_IN_FLIGHT_KEY } from '../Worklist/WorklistPageV2/constants';
import { WorklistFilters } from '../WorklistFilters/types';
import { hasSameItems, joinSmidsToString } from '../Worklist/utils/worklistUtils';
import { isAnyProvidedCannedWorklist } from '../Worklist/utils/isProvidedCannedWorklist';
import { useCustomWorklists } from '../Worklist/hooks/useCustomWorklists';

export type WorklistFiltersContextProps = Readonly<{
  defaultSurface?: WorklistSurfaceType;
  children: React.ReactNode;
  overrideURLSearchParams?: URLSearchParams | null | undefined;
  overrideSurface?: WorklistSurfaceType | null | undefined;
  defaultCustomWorklistIDs?: string[];
}>;

type WorklistFiltersContextBag = {
  surface: WorklistSurfaceType;
  updateSurface: (arg1: WorklistSurfaceType) => void;
  availableFilters: FilterDefinitionType;
  filters: FilterStateType;
  lockedFilters: FilterStateType;
  updateFilters: (arg1: FilterStateType) => void;
  replaceFilters: (arg1: FilterStateType) => void;
  resetAllFilters: () => void;
  resetFilter: (arg1: WorklistFiltersType) => void;
  sortColumns: Array<SortColumn>;
  sortOrders: Array<SortOrder>;
  onSortChange: (columnId: string[], order: Array<SortOrder>) => void;
  setSearchForSurface: (value: string) => void;
  debouncedSearchForSurface: string;
  searchForSurface: string;
  worklistViews: GetWorklistViewsQuery['worklistViews'];
  areWorklistViewsLoading: boolean;
  getAppliedFilters: () => Array<string>;
  filtersBySurface: FilterStateBySurface;
  debouncedSearches: WorklistSurfaceSearchValues;
  surfacesToDefaultFilters: {
    [key: string]: FilterDefinitionType;
  };
  surfacesToLockedFilters: {
    [key: string]: FilterStateType;
  };
  updateCombinedCustomWorklists: (activeWorklistIds: string[]) => Promise<void>;
  updateSurfaceAndCustomWorklist: (customWorklistID: string) => Promise<void>;
  updateSurfaceAndSearch: (newSurface: WorklistSurfaceType, newSearch: string) => void;
  areCustomWorklistFiltersLoading: boolean;
  isStillProcessingCustomWorklistFilters: MutableRefObject<boolean>;
};

const WorklistFiltersContext = createContext<WorklistFiltersContextBag | null | undefined>(
  undefined
);

export const WorklistFiltersContextProvider = ({
  children,
  defaultSurface = WorklistSurface.PENDING,
  defaultCustomWorklistIDs = [],
  overrideURLSearchParams,
  overrideSurface,
}: WorklistFiltersContextProps): React.ReactElement => {
  const [isCustomWorklistsEnabled] = useFeatureFlagEnabled(FF.CUSTOM_WORKLISTS);
  const [isQcStatusColumnEnabled] = useFeatureFlagEnabled(FF.QC_STATUS_COLUMN);

  const {
    surface: urlSurface,
    sortColumn: urlSortColumns,
    sortOrder: urlSortOrders,
    filters: urlFilters,
  } = useWorklistURLParams(overrideURLSearchParams);

  const urlFiltersOnLoad = useRef(urlFilters);
  const currentCustomWorklistRequest = useRef<string | null>(null);
  const hasMountedRef = useRef(false);

  const isStillProcessingCustomWorklistFilters = useRef(false);
  const [activeCustomWorklistID, setActiveCustomWorklistID] = useRecoilState(
    activeCustomWorklistIDState
  );
  const setActiveCustomWorklistFilters = useSetRecoilState(activeCustomWorklistFiltersState);
  const { allWorklists } = useCustomWorklists();
  const activeCustomWorklistNames = useMemo(() => {
    if (activeCustomWorklistID == null || activeCustomWorklistID.length === 0) {
      return [];
    }
    return getCustomWorklistNames(activeCustomWorklistID, allWorklists).filter(
      (name): name is string => name != null
    );
  }, [activeCustomWorklistID, allWorklists]);

  const initialSurface: WorklistSurfaceType = (() => {
    if (
      defaultSurface === WorklistSurface.GLOBAL_SEARCH ||
      defaultSurface === WorklistSurface.STUDIES_DIALOG
    ) {
      return defaultSurface;
    }

    if (isCustomWorklistsEnabled && defaultCustomWorklistIDs.length > 0) {
      return WorklistSurface.CUSTOM_WORKLIST;
    }
    if (urlSurface != null) {
      return urlSurface;
    }
    if (isCustomWorklistsEnabled) {
      return WorklistSurface.PLACEHOLDER;
    }

    return defaultSurface;
  })();

  const { surfaceToFilterConfig } = useGenerateFilterConfig(defaultSurface);

  const [surface, setSurface] = useState<WorklistSurfaceType>(initialSurface);

  const [sortColumns, setSortColumns] = useState<Array<SortColumn>>(urlSortColumns);
  const [sortOrders, setSortOrders] = useState<Array<SortOrder>>(urlSortOrders);

  const { worklistViews, areWorklistViewsLoading } = useWorklistViews();
  const { saveSortingPreferences, savedSortingPreferences } = useSavedSortingPreferences();
  const { saveLastViewedCustomWorklists } = useSaveLastViewedCustomWorklists();
  const [getCustomWorklistDetails, { loading: areCustomWorklistFiltersLoading }] = useLazyQuery(
    GET_CUSTOM_WORKLIST,
    {
      fetchPolicy: 'cache-first',
    }
  );

  useEffect(() => {
    if (overrideSurface != null) {
      setSurface(overrideSurface);
    }
  }, [overrideSurface]);

  useEffect(() => {
    setSortColumns(urlSortColumns);
    setSortOrders(urlSortOrders);
  }, [urlSortColumns, urlSortOrders]);

  const maybeSaveSortingPreferencesWithCustomWorklist = useCallback(
    ({ surface: savedSurface, ...rest }: SaveSortingPreferencesArgs) => {
      saveSortingPreferences({
        ...rest,
        surface: savedSurface,
        worklistViewID:
          savedSurface === WorklistSurface.CUSTOM_WORKLIST && activeCustomWorklistID.length > 0
            ? activeCustomWorklistID
            : [],
      });
    },
    [activeCustomWorklistID, saveSortingPreferences]
  );

  const fetchDefaultCustomWorklistFilters = async () => {
    if (hasMountedRef.current) return;
    hasMountedRef.current = true;

    if (
      defaultCustomWorklistIDs.length === 0 ||
      (activeCustomWorklistID.length > 0 &&
        hasSameItems(activeCustomWorklistID, defaultCustomWorklistIDs))
    ) {
      return;
    }

    isStillProcessingCustomWorklistFilters.current = true;

    // This should already be set in the parent, but just in case, we set it here as well
    if (activeCustomWorklistID.length === 0) {
      setActiveCustomWorklistID(defaultCustomWorklistIDs);
    }

    if (defaultCustomWorklistIDs.length > 1) {
      logger.info(
        `[WorklistFiltersContext] Fetching combined custom worklists: ${defaultCustomWorklistIDs}`
      );
      updateCombinedCustomWorklists(defaultCustomWorklistIDs);
      isStillProcessingCustomWorklistFilters.current = false;
      return;
    }

    const customWorklistID = defaultCustomWorklistIDs[0] ?? null;

    updateSurface(WorklistSurface.CUSTOM_WORKLIST, customWorklistID);
    logger.info(
      `[WorklistFiltersContext] Fetching default custom worklist filters for ${customWorklistID}`,
      {
        defaultCustomWorklistIDs: customWorklistID,
      }
    );

    const { data: customWorklistData } = await getCustomWorklistDetails({
      variables: { smid: customWorklistID ?? '' },
      fetchPolicy: 'cache-first',
    });

    const filters = formatCustomWorklistFilters(customWorklistData?.customWorklist?.filters || []);

    setActiveCustomWorklistFilters(filters);

    // If overrideURLSearchParams has more than one entry, it means that
    // the user has saved sorting and filter preferences for this worklist
    // and we should not override with the default custom worklist filters
    if (
      !isCustomWorklistsEnabled ||
      overrideURLSearchParams == null ||
      overrideURLSearchParams?.size < 1
    ) {
      setFiltersBySurface((prevFilters) => ({
        ...prevFilters,
        [WorklistSurface.CUSTOM_WORKLIST]: filters,
      }));
    }
    isStillProcessingCustomWorklistFilters.current = false;
  };

  useMount(() => {
    fetchDefaultCustomWorklistFilters();
  });

  const surfacesToDefaultFilters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(surfaceToFilterConfig).map(([surface, { defaultFilters }]: [any, any]) => [
          surface,
          defaultFilters,
        ])
      ),
    [surfaceToFilterConfig]
  );

  const surfacesToLockedFilters: {
    [key: string]: FilterStateType;
  } = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(surfaceToFilterConfig).map(([surface, { lockedFilters }]: [any, any]) => [
          surface,
          lockedFilters,
        ])
      ),
    [surfaceToFilterConfig]
  );

  const initialFilters: FilterStateType = useMemo(() => {
    if (urlFiltersOnLoad.current == null) {
      return {};
    }
    const urlFiltersToApply = urlFiltersOnLoad.current;
    const surfaceFilters = surfacesToDefaultFilters[surface];
    return parseFiltersFromURLParams(urlFiltersToApply, surfaceFilters);
  }, [surfacesToDefaultFilters, surface]);

  const [filtersBySurface, setFiltersBySurface] = useState<FilterStateBySurface>(() => {
    const initialState = {
      ...DEFAULT_EMPTY_WORKLIST_SURFACE_FILTERS,
      [surface as string]: {
        ...initialFilters,
        ...Object.fromEntries(
          Object.entries(surfacesToDefaultFilters[surface] ?? {})
            .filter(
              ([, filterConfig]: [any, any]) =>
                filterConfig.type === FilterVariant.Toggle && filterConfig.defaultValue === true
            )
            .map(([key, filterConfig]: [any, any]) => [key, filterConfig.defaultValue])
        ),
      },
    };

    if (surface === WorklistSurface.QC) {
      initialState[WorklistSurface.QC] = {
        qcStatus: isQcStatusColumnEnabled
          ? surfacesToDefaultFilters[WorklistSurface.QC].qcStatus?.items
              ?.filter((i: MultiSelectFilterItemType) => i.defaultSelected)
              .map((i: MultiSelectFilterItemType) => i.value)
          : [WorklistItemStatus.Qc],
      };
    }

    return initialState;
  });

  const currentFilterValues = useMemo(() => filtersBySurface[surface], [filtersBySurface, surface]);

  const onSortChange = useCallback(
    (columnIds: string[], orders: Array<SortOrder>) => {
      const mappedColumns: Array<SortColumn> = columnIds.map(
        // @ts-expect-error [EN-10263] - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly priority: "PRIORITY"; readonly updated: "UPDATED"; readonly date: "DATE"; readonly modality: "MODALITY"; readonly studyDescription: "STUDY_DESCRIPTION"; readonly status: "STATUS"; ... 14 more ...; readonly firstVerifiedStatusDate: "FIRST_VERIFIED_STATUS_DATETIME"; }'.
        (id) => SORTABLE_WORKLIST_COLUMN_MAP[id]
      );

      let changedColumns: boolean = !equals(mappedColumns, sortColumns);
      let changedOrders: boolean = !equals(orders, sortOrders);

      if (changedColumns) {
        setSortColumns(mappedColumns);
        changedColumns = true;
      }

      if (changedOrders) {
        setSortOrders(orders);
        changedOrders = true;
      }

      if (surface === WorklistSurface.STUDIES_DIALOG) {
        return;
      }

      if (changedColumns || changedOrders) {
        maybeSaveSortingPreferencesWithCustomWorklist({
          surface,
          columns: mappedColumns.join(','),
          orders: orders.join(','),
          filters: currentFilterValues,
        });
      }
    },
    [
      sortColumns,
      sortOrders,
      surface,
      maybeSaveSortingPreferencesWithCustomWorklist,
      currentFilterValues,
    ]
  );

  const updateFilters = useCallback(
    (newFilters: FilterStateType) => {
      const previousFilters = filtersBySurface[surface];

      setFiltersBySurface((prevFilters) => ({
        ...prevFilters,
        [surface as string]: { ...prevFilters[surface], ...newFilters },
      }));

      maybeSaveSortingPreferencesWithCustomWorklist({
        surface,
        columns: sortColumns.join(','),
        orders: sortOrders.join(','),
        filters: { ...previousFilters, ...newFilters },
      });

      logger.info(
        `[WorklistFiltersContext] Filters for ${surface}${activeCustomWorklistID.length > 0 ? ' with customWorklistID ' + activeCustomWorklistID : ''} have been updated`,
        {
          surface,
          oldFilters: previousFilters,
          filters: { ...previousFilters, ...newFilters },
        }
      );
    },
    [
      filtersBySurface,
      surface,
      maybeSaveSortingPreferencesWithCustomWorklist,
      sortColumns,
      sortOrders,
      activeCustomWorklistID,
    ]
  );

  const replaceFilters = useCallback(
    (newFilters: FilterStateType) => {
      setFiltersBySurface((prevFilters) => ({
        ...prevFilters,
        [surface as string]: newFilters,
      }));

      maybeSaveSortingPreferencesWithCustomWorklist({
        surface,
        columns: sortColumns.join(','),
        orders: sortOrders.join(','),
        filters: newFilters,
      });

      logger.info(
        `[WorklistFiltersContext] Filters for ${surface}${activeCustomWorklistID.length > 0 ? ' with customWorklistID ' + activeCustomWorklistID : ''} have been replaced`,
        {
          surface,
          oldFilters: filtersBySurface[surface],
          filters: newFilters,
        }
      );
    },
    [
      filtersBySurface,
      surface,
      maybeSaveSortingPreferencesWithCustomWorklist,
      sortColumns,
      sortOrders,
      activeCustomWorklistID,
    ]
  );

  const { saveWorklistViews } = useSavedWorklistViews({
    surface,
    filters: { ...filtersBySurface[surface], ...surfacesToLockedFilters[surface] },
    updateFilters,
  });

  const [searches, debouncedSearches, setSearches] = useDebouncedState<WorklistSurfaceSearchValues>(
    DEFAULT_WORKLIST_SURFACE_SEARCH_VALUES,
    SEARCH_DEBOUNCE_MS
  );

  const debouncedSearchForSurface = useMemo(
    () => debouncedSearches[surface],
    [debouncedSearches, surface]
  );
  const searchForSurface = useMemo(() => searches[surface], [searches, surface]);

  const setSearchForSurface = useCallback(
    (value: string) => setSearches((currentSearch) => assoc(surface, value, currentSearch)),
    [setSearches, surface]
  );

  const resetAllFilters = useCallback(() => {
    logger.info(
      `[WorklistFiltersContext] Resetting all filters for ${surface}${activeCustomWorklistID ? ' with customWorklistID ' + activeCustomWorklistID : ''}`
    );

    setFiltersBySurface((prevFilters) => ({
      ...prevFilters,
      [surface as string]: surfacesToLockedFilters[surface],
    }));
    saveWorklistViews([]);
    setSearchForSurface('');

    maybeSaveSortingPreferencesWithCustomWorklist({
      surface,
      columns: sortColumns.join(','),
      orders: sortOrders.join(','),
      filters: {},
    });
  }, [
    activeCustomWorklistID,
    maybeSaveSortingPreferencesWithCustomWorklist,
    saveWorklistViews,
    setSearchForSurface,
    sortColumns,
    sortOrders,
    surface,
    surfacesToLockedFilters,
  ]);

  const resetFilter = useCallback(
    (filterKey: WorklistFiltersType) => {
      const previousFilters = filtersBySurface[surface];
      delete previousFilters[filterKey];

      logger.info(
        `[WorklistFiltersContext] Resetting ${filterKey} filter for ${surface}${activeCustomWorklistID ? ' with customWorklistID ' + activeCustomWorklistID : ''}`
      );

      maybeSaveSortingPreferencesWithCustomWorklist({
        surface,
        columns: sortColumns.join(','),
        orders: sortOrders.join(','),
        filters: previousFilters,
      });

      setFiltersBySurface((prevFilters) => {
        const newFilters = { ...prevFilters[surface] } as const;
        delete newFilters[filterKey];
        return { ...prevFilters, [surface as string]: newFilters };
      });
    },
    [
      activeCustomWorklistID,
      filtersBySurface,
      maybeSaveSortingPreferencesWithCustomWorklist,
      sortColumns,
      sortOrders,
      surface,
    ]
  );

  const loadSavedSortingPreferences = useCallback(
    ({ worklistIDs, surface }: { worklistIDs: string[]; surface: WorklistSurfaceType }): void => {
      let newSortColumn: Array<SortColumn> = DEFAULT_SORT_COLUMNS;
      let newSortOrder: Array<SortOrder> = DEFAULT_SORT_ORDERS;

      if (
        [WorklistSurface.RECENTLY_READ, WorklistSurface.ARCHIVE].some((comparatorSurface) =>
          isAnyProvidedCannedWorklist({
            currentSurface: surface,
            comparatorSurface,
            customWorklistNames: activeCustomWorklistNames,
          })
        )
      ) {
        newSortColumn = [SortColumn.SubmittedAt];
        newSortOrder = [SortOrder.Desc];
      } else if (sortColumns.includes(SortColumn.SubmittedAt)) {
        newSortColumn = [SortColumn.StudyDatetime];
        newSortOrder = [SortOrder.Asc];
      }

      const savedPreferenceKey =
        worklistIDs.length > 0 ? `${surface}_${joinSmidsToString(worklistIDs)}` : surface;

      if (savedSortingPreferences[savedPreferenceKey] != null) {
        const params = new URLSearchParams(savedSortingPreferences[savedPreferenceKey]);
        const column = params.get('sortColumn') ?? '';
        const order = params.get('sortOrder') ?? '';
        newSortColumn = validateSortColumns(column.split(','), newSortColumn);
        newSortOrder = validateSortOrders(order.split(','), newSortOrder);
      }

      setSurface(surface);
      logger.info(
        `[WorklistFiltersContext] Loading saved sorting preferences for ${surface} with worklistIDs: ${worklistIDs}`
      );
      if (newSortColumn != null && newSortOrder != null) {
        setSortColumns(newSortColumn);
        setSortOrders(newSortOrder);
      }
    },
    [activeCustomWorklistNames, savedSortingPreferences, sortColumns]
  );

  const updateSurface = useCallback(
    (newSurface: WorklistSurfaceType, customWorklistID?: string) => {
      logger.info(
        `[WorklistFiltersContext] Updating surface from ${surface}${activeCustomWorklistID[0] ? ' with customWorklistID ' + activeCustomWorklistID[0] : ''} to ${newSurface}${
          customWorklistID ? ' with customWorklistID ' + customWorklistID : ''
        }`
      );

      if (newSurface === surface && activeCustomWorklistID[0] === customWorklistID) {
        return;
      }

      loadSavedSortingPreferences({
        worklistIDs: customWorklistID ? [customWorklistID] : [],
        surface: newSurface,
      });

      // This is only here to support the legacy worklists. We will have something
      // separate in the future for custom worklists
      if (
        !(
          [
            WorklistSurface.GLOBAL_SEARCH,
            WorklistSurface.PLACEHOLDER,
            WorklistSurface.CUSTOM_WORKLIST,
          ] as WorklistSurfaceType[]
        ).includes(surface)
      ) {
        saveLastViewedCustomWorklists([
          {
            surface: newSurface,
            worklistViewID: [],
          },
        ]);
      }
    },
    [activeCustomWorklistID, loadSavedSortingPreferences, saveLastViewedCustomWorklists, surface]
  );

  const updateSurfaceAndCustomWorklist = useCallback(
    async (customWorklistID: string) => {
      if (!isCustomWorklistsEnabled || customWorklistID == null) {
        return;
      }

      isStillProcessingCustomWorklistFilters.current = true;
      currentCustomWorklistRequest.current = customWorklistID;

      setFiltersBySurface((prevFilters) => ({
        ...prevFilters,
        [WorklistSurface.CUSTOM_WORKLIST]: { [CUSTOM_WORKLIST_FILTERS_IN_FLIGHT_KEY]: ['true'] },
      }));

      updateSurface(WorklistSurface.CUSTOM_WORKLIST, customWorklistID);

      logger.info(
        `[WorklistFiltersContext] Switched to custom worklist ${customWorklistID} from ${activeCustomWorklistID.length > 0 ? activeCustomWorklistID : 'none'}`
      );

      let filtersToApply = {};

      const { data: customWorklistData } = await getCustomWorklistDetails({
        variables: { smid: customWorklistID },
        fetchPolicy: 'cache-first',
      });

      // Ignore if a newer request has started
      if (currentCustomWorklistRequest.current !== customWorklistID) {
        return;
      }

      const customWorklistFilters = formatCustomWorklistFilters(
        customWorklistData?.customWorklist?.filters || []
      );

      const savedPreferences =
        savedSortingPreferences[`${WorklistSurface.CUSTOM_WORKLIST}_${customWorklistID}`];

      if (savedPreferences != null) {
        const { filters } = getWorklistURLParams(new URLSearchParams(savedPreferences));

        if (filters != null) {
          filtersToApply = parseFiltersFromURLParams(
            filters,
            surfacesToDefaultFilters[WorklistSurface.CUSTOM_WORKLIST]
          );
          logger.info(
            `[WorklistFiltersContext] Custom worklist ${customWorklistID} has saved preferences`,
            { filters: filtersToApply }
          );
        }
      } else {
        filtersToApply = customWorklistFilters;
        logger.info(
          `[WorklistFiltersContext] Custom worklist ${customWorklistID} has no saved preferences, using filters from definition`,
          { filters: filtersToApply }
        );
      }

      setFiltersBySurface((prevFilters) => ({
        ...prevFilters,
        [WorklistSurface.CUSTOM_WORKLIST]: filtersToApply,
      }));

      setActiveCustomWorklistFilters(customWorklistFilters);
      isStillProcessingCustomWorklistFilters.current = false;
    },
    [
      activeCustomWorklistID,
      getCustomWorklistDetails,
      isCustomWorklistsEnabled,
      savedSortingPreferences,
      setActiveCustomWorklistFilters,
      surfacesToDefaultFilters,
      updateSurface,
    ]
  );

  const updateCombinedCustomWorklists = useCallback(
    async (activeWorklistIds: string[]) => {
      isStillProcessingCustomWorklistFilters.current = true;
      setActiveCustomWorklistFilters({});

      setFiltersBySurface((prevFilters) => ({
        ...prevFilters,
        [WorklistSurface.CUSTOM_WORKLIST]: { [CUSTOM_WORKLIST_FILTERS_IN_FLIGHT_KEY]: ['true'] },
      }));

      loadSavedSortingPreferences({
        worklistIDs: activeWorklistIds,
        surface: WorklistSurface.CUSTOM_WORKLIST,
      });

      const savedPreferenceKey = `${surface}_${joinSmidsToString(activeWorklistIds)}`;
      const savedPreferences = savedSortingPreferences[savedPreferenceKey];
      let filtersToApply = {};

      if (savedPreferences != null) {
        const { filters } = getWorklistURLParams(new URLSearchParams(savedPreferences));

        if (filters != null) {
          filtersToApply = parseFiltersFromURLParams(
            filters,
            surfacesToDefaultFilters[WorklistSurface.CUSTOM_WORKLIST]
          );
          logger.info(
            `[WorklistFiltersContext] Combined Custom worklist ${joinSmidsToString(activeWorklistIds)} has saved preferences`,
            { filters: filtersToApply }
          );
        }
      } else {
        filtersToApply = {
          [WorklistFilters.Views]: activeWorklistIds,
        };
      }

      setFiltersBySurface((prevFilters) => ({
        ...prevFilters,
        [WorklistSurface.CUSTOM_WORKLIST]: filtersToApply,
      }));
      isStillProcessingCustomWorklistFilters.current = false;
    },
    [
      loadSavedSortingPreferences,
      savedSortingPreferences,
      setActiveCustomWorklistFilters,
      surface,
      surfacesToDefaultFilters,
    ]
  );

  const updateSurfaceAndSearch = useCallback(
    (newSurface: WorklistSurfaceType, newSearch: string) => {
      logger.info(
        `[WorklistFiltersContext] Updating  surface to ${newSurface} with search "${newSearch}"`
      );

      setSearches((currentSearch) => assoc(newSurface, newSearch, currentSearch));
      updateSurface(newSurface);
    },
    [setSearches, updateSurface]
  );

  const getAppliedFilters = useCallback(() => {
    const filters = { ...filtersBySurface[surface], ...surfacesToLockedFilters[surface] } as const;
    const nonEmptyFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]: [any, any]) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }

        return value != null && value !== '';
      })
    );

    return Object.keys(nonEmptyFilters);
  }, [filtersBySurface, surfacesToLockedFilters, surface]);

  const currentFilters = useMemo(
    () => ({ ...filtersBySurface[surface], ...surfacesToLockedFilters[surface] }),
    [filtersBySurface, surface, surfacesToLockedFilters]
  );

  const contextBag = useMemo(
    () => ({
      sortColumns,
      sortOrders,
      filters: currentFilters,
      lockedFilters: surfacesToLockedFilters[surface],
      updateFilters,
      replaceFilters,
      surface,
      updateSurface,
      setSearchForSurface,
      searchForSurface,
      debouncedSearchForSurface,
      onSortChange,
      availableFilters: surfacesToDefaultFilters[surface],
      resetAllFilters,
      resetFilter,
      worklistViews,
      areWorklistViewsLoading,
      getAppliedFilters,
      filtersBySurface,
      debouncedSearches,
      surfacesToDefaultFilters,
      updateCombinedCustomWorklists,
      updateSurfaceAndCustomWorklist,
      updateSurfaceAndSearch,
      surfacesToLockedFilters,
      areCustomWorklistFiltersLoading,
      isStillProcessingCustomWorklistFilters,
    }),
    [
      areWorklistViewsLoading,
      currentFilters,
      debouncedSearchForSurface,
      debouncedSearches,
      filtersBySurface,
      getAppliedFilters,
      onSortChange,
      resetAllFilters,
      resetFilter,
      searchForSurface,
      setSearchForSurface,
      sortColumns,
      sortOrders,
      surface,
      surfacesToDefaultFilters,
      surfacesToLockedFilters,
      updateFilters,
      replaceFilters,
      updateSurface,
      updateCombinedCustomWorklists,
      updateSurfaceAndCustomWorklist,
      updateSurfaceAndSearch,
      worklistViews,
      areCustomWorklistFiltersLoading,
    ]
  );
  return (
    <WorklistFiltersContext.Provider value={contextBag}>{children}</WorklistFiltersContext.Provider>
  );
};

export const useWorklistFiltersContext = (): WorklistFiltersContextBag => {
  const worklistFilters = useContext(WorklistFiltersContext);

  if (!worklistFilters) {
    throw new Error(
      'useWorklistFiltersContext must be used within a WorklistFiltersContextProvider.'
    );
  }

  return worklistFilters;
};
